<template>
  <div
    :id="'block-' + id"
    :ref="'block-' + id"
    class="col-span-1 shadow-xl flex flex-col text-center bg-white rounded-xl relative border-4 h-auto overflow-hidden"
    :class="['bg-' + color + '-100 border-' + color + '-900', {'opacity-0': isNew}]"
  >
    <div v-if="hasError"
         class="absolute w-full h-full bg-red-200 opacity-90 z-50 text-red-900 font-medium pt-5"
    >
      Oh no, something went wrong!<br />Please <a @click="reload()" class="underline cursor-pointer">reload</a>.
    </div>

    <div class="absolute z-10 w-full h-full">
      <div
        class="rounded-md h-full opacity-20 border-3"
        style="border-radius: 8px;"
        :style="'box-shadow: inset 0 0 100px 0 #' + colorDeep + '; border-color: #' + colorLight"
      ></div>
    </div>

    <div
      class="z-20 px-4 pt-4 relative shadow-none flex items-center"
      :class="'bg-transparent border-' + color + '-300'"
    >
      <div class="flex-shrink-0">
        <span
          class="rounded-lg inline-flex p-2"
          :class="'bg-' + color + '-50 border-' + color + '-300 border-2'"
        >
          <BlockIcon
            @click="buildMode ? blockEdit() : null"
            v-bind:type="faceType"
            v-bind:color="color"
            v-bind:isLoading="isLoading"
            v-bind:hasError="hasError"
          ></BlockIcon>
        </span>
      </div>

      <div class="flex-1 text-left pl-2">
        <svg
          @click="blockEdit"
          :key="titleKey"
          class="h-1 cursor-pointer z-10 text-gray-900 text-lg font-bold inline-block"
          style="font-family: 'Roboto Mono', monospace;width: 100%; height: 28px;"
        >
          <text
            :style="'stroke: #' + colorOutline + ';'"
            text-anchor="left"
            x="1"
            y="22"
            fill="white"
            stroke-width="4"
            letter-spacing="2"
            paint-order="stroke"
          >{{ title }}</text>
        </svg>
        <div class="text-xs leading-none" :class="'text-' + color + '-700'">
          <span class="font-medium">{{ doneCount }}/{{ totalCount }}</span> done
          <span v-if="streakCount" class="ml-2">· streak {{ streakCount }}</span>
        </div>
      </div>

      <button
        v-if="buildMode"
        @click="blockEdit"
        class="z-20 top-1 left-1 absolute opacity-50 hover:opacity-100 rounded-md inline-flex text-gray-900 hover:text-gray-1000 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        :class="'text-' + color + '-900'"
      >
        <svg class="w-4 h-5" fill="currentColor" viewBox="0 0 20 20" xmlns="http://www.w3.org/2000/svg"><path d="M17.414 2.586a2 2 0 00-2.828 0L7 10.172V13h2.828l7.586-7.586a2 2 0 000-2.828z"></path><path fill-rule="evenodd" d="M2 6a2 2 0 012-2h4a1 1 0 010 2H4v10h10v-4a1 1 0 112 0v4a2 2 0 01-2 2H4a2 2 0 01-2-2V6z" clip-rule="evenodd"></path></svg>
      </button>

      <button
        v-if="buildMode"
        class="cursor-move block-drag-handle z-20 top-3 left-1/2 transform -translate-x-1/2 -translate-y-1/2 absolute opacity-50 hover:opacity-100 rounded-md inline-flex text-gray-900 hover:text-gray-1000 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        :class="'text-' + color + '-900'"
      >
        <svg class="w-6 h-6" stroke="currentColor" fill="none" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round">
          <path stroke="none" d="M0 0h24v24H0z" fill="none"/><circle cx="5" cy="9" r="1" /><circle cx="5" cy="15" r="1" /><circle cx="12" cy="9" r="1" /><circle cx="12" cy="15" r="1" /><circle cx="19" cy="9" r="1" /><circle cx="19" cy="15" r="1" />
        </svg>
      </button>

      <button
        v-if="buildMode"
        @click="deleteBlock"
        class="z-20 top-1 right-1 absolute opacity-50 hover:opacity-100 rounded-md inline-flex text-gray-900 hover:text-gray-1000 focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-gray-500"
        :class="'text-' + color + '-900'"
      >
        <span class="sr-only">Close</span>
        <svg class="h-5 w-5" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 20 20" fill="currentColor" aria-hidden="true">
          <path fill-rule="evenodd" d="M4.293 4.293a1 1 0 011.414 0L10 8.586l4.293-4.293a1 1 0 111.414 1.414L11.414 10l4.293 4.293a1 1 0 01-1.414 1.414L10 11.414l-4.293 4.293a1 1 0 01-1.414-1.414L8.586 10 4.293 5.707a1 1 0 010-1.414z" clip-rule="evenodd" />
        </svg>
      </button>
    </div>

    <div class="z-20 px-4 pb-3 pt-3">
      <div class="w-full rounded-md overflow-hidden bg-white/40">
        <div
          class="h-2"
          :class="'bg-' + color + '-300'"
          :style="{ width: progressPercent + '%' }"
          style="transition: width 0.25s ease;"
        ></div>
      </div>

      <div class="mt-3 space-y-2 text-left">
        <div
          v-for="todo in todosSafe"
          :key="todo.id"
          class="flex items-start"
        >
          <button
            type="button"
            class="mt-0.5 w-5 h-5 flex-none rounded border-2 flex items-center justify-center"
            :class="todo.done ? 'bg-' + color + '-300 border-' + color + '-700' : 'bg-white/50 border-' + color + '-400'"
            @click="toggleTodo(todo.id)"
          >
            <svg v-if="todo.done" xmlns="http://www.w3.org/2000/svg" class="h-4 w-4" viewBox="0 0 20 20" fill="currentColor">
              <path fill-rule="evenodd" d="M16.707 5.293a1 1 0 010 1.414l-8 8a1 1 0 01-1.414 0l-4-4a1 1 0 011.414-1.414L8 12.586l7.293-7.293a1 1 0 011.414 0z" clip-rule="evenodd" />
            </svg>
          </button>

          <div class="flex-1 pl-2 text-sm leading-snug" :class="todo.done ? 'line-through text-' + color + '-700 opacity-70' : 'text-' + color + '-900'">
            {{ todo.text }}
          </div>
        </div>

        <div class="flex items-center justify-between pt-2">
          <button
            type="button"
            @click="resetList"
            class="text-xs underline"
            :class="'text-' + color + '-900'"
            :disabled="preview || totalCount === 0"
          >
            Reset list
          </button>

          <div v-if="lastHistory" class="text-[10px] opacity-70" :class="'text-' + color + '-900'">
            Last: {{ lastHistory.completed }}/{{ lastHistory.total }}
          </div>
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import {colors} from '../colors/colors.js';
import BlockIcon from './BlockIcon.vue';
import {useToast} from 'vue-toastification';
import {v4 as uuidv4} from 'uuid';
import anime from 'animejs';

function normalizeTodos(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((t) => {
      if (!t || typeof t !== 'object') return null;
      const id = t.id ? String(t.id) : uuidv4();
      const text = typeof t.text === 'string' ? t.text : '';
      const done = !!t.done;
      const doneAt = typeof t.doneAt === 'number' ? t.doneAt : null;
      if (!text) return null;
      return { id, text, done, doneAt };
    })
    .filter(Boolean);
}

function normalizeHistory(value) {
  if (!Array.isArray(value)) return [];
  return value
    .map((h) => {
      if (!h || typeof h !== 'object') return null;
      const ts = typeof h.ts === 'number' ? h.ts : Date.now();
      const total = typeof h.total === 'number' ? h.total : 0;
      const completed = typeof h.completed === 'number' ? h.completed : 0;
      return { ts, total, completed };
    })
    .filter(Boolean);
}

export default {
  name: 'BlockTodoList',
  components: {
    BlockIcon,
  },
  props: {
    id: String,
    title: String,
    color: String,
    size: Number,
    src: String,
    faceType: {
      default: 'default',
      type: String,
    },
    todos: {
      type: Array,
      default: () => [],
    },
    todoHistory: {
      type: Array,
      default: () => [],
    },
    preview: {
      default: false,
      type: Boolean,
    },
    buildMode: {
      default: false,
      type: Boolean,
    },
    embed: {
      default: false,
      type: Boolean,
    },
    embedPreview: {
      default: false,
      type: Boolean,
    },
    local: {
      default: false,
      type: Boolean,
    },
    isLoading: {
      default: false,
      type: Boolean,
    },
    hasError: {
      default: false,
      type: Boolean,
    },
    isNew: {
      default: false,
      type: Boolean,
    },
    embedOptions: Object,
    refID: String,
  },
  setup() {
    const toast = useToast();
    return { toast };
  },
  data() {
    return {
      colors,
      titleKey: uuidv4(),
    };
  },
  computed: {
    todosSafe() {
      return normalizeTodos(this.todos);
    },
    doneCount() {
      return this.todosSafe.filter(t => t.done).length;
    },
    totalCount() {
      return this.todosSafe.length;
    },
    progressPercent() {
      if (!this.totalCount) return 0;
      return Math.round((this.doneCount / this.totalCount) * 100);
    },
    historySafe() {
      return normalizeHistory(this.todoHistory);
    },
    lastHistory() {
      if (!this.historySafe.length) return null;
      return this.historySafe[this.historySafe.length - 1];
    },
    streakCount() {
      // “Cycle streak”: consecutive resets with all todos completed.
      const h = this.historySafe;
      if (!h.length) return 0;
      let streak = 0;
      for (let i = h.length - 1; i >= 0; i--) {
        const entry = h[i];
        if (!entry.total) break;
        if (entry.completed !== entry.total) break;
        streak++;
      }
      return streak;
    },
    colorLight() {
      const idx = this.colors.findIndex(item => item.value === this.color);
      return this.colors[idx]?.colors?.[100] || 'ffffff';
    },
    colorDeep() {
      const idx = this.colors.findIndex(item => item.value === this.color);
      return this.colors[idx]?.colors?.[600] || '000000';
    },
    colorOutline() {
      const idx = this.colors.findIndex(item => item.value === this.color);
      return this.colors[idx]?.colors?.[900] || '000000';
    },
  },
  methods: {
    reload() {
      window.location.reload();
    },
    blockEdit() {
      if (this.preview) {
        this.toast.warning('Please save your changes first to be able to edit the block.');
      } else {
        this.$emit('blockEditStart', this.id);
      }
    },
    deleteBlock() {
      if (this.preview) {
        this.toast.warning('Please save your changes first to be able to edit the block.');
      } else {
        this.$emit('blockDelete', this.id, this.refID);
      }
    },
    emitUpdate(data) {
      if (this.preview) return;
      this.$emit('blockUpdate', this.refID, this.id, data);
    },
    toggleTodo(todoId) {
      if (this.preview) return;
      const todos = normalizeTodos(this.todos);
      const idx = todos.findIndex(t => t.id === todoId);
      if (idx === -1) return;

      const now = Date.now();
      todos[idx].done = !todos[idx].done;
      todos[idx].doneAt = todos[idx].done ? now : null;

      this.emitUpdate({ todos });
    },
    resetList() {
      if (this.preview) return;
      const todos = normalizeTodos(this.todos);
      const total = todos.length;
      const completed = todos.filter(t => t.done).length;

      const history = normalizeHistory(this.todoHistory);
      history.push({ ts: Date.now(), total, completed });
      // Keep history bounded.
      if (history.length > 50) history.splice(0, history.length - 50);

      todos.forEach((t) => {
        t.done = false;
        t.doneAt = null;
      });

      this.emitUpdate({ todos, todoHistory: history });

      // Subtle animation for feedback.
      anime({
        targets: '#block-' + this.id,
        duration: 180,
        easing: 'easeInOutQuad',
        scale: [1, 0.99, 1],
      });
    },
  },
  mounted() {
    if (this.isNew) {
      this.$emit('blockAdded', this.id);

      const block = document.querySelector('#block-' + this.id);
      if (block) block.style.zIndex = 10000000;

      // Keep the same add animation as other blocks.
      anime({
        targets: '#block-' + this.id,
        easing: 'linear',
        duration: 380,
        opacity: [1, 1],
        rotate: [3, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0, 0],
        delay: 600,
        matrix3d: [
          '0.1, 0, 0, 0, 0, 0.1, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '0.318, 0, 0, 0, 0, 0.318, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '0.448, 0, 0, 0, 0, 0.448, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '0.688, 0, 0, 0, 0, 0.688, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '0.983, 0, 0, 0, 0, 0.983, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '1.011, 0, 0, 0, 0, 1.011, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '1.131, 0, 0, 0, 0, 1.131, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '1.147, 0, 0, 0, 0, 1.147, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '1.056, 0, 0, 0, 0, 1.056, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '1.129, 0, 0, 0, 0, 1.129, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '1.097, 0, 0, 0, 0, 1.097, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '1.048, 0, 0, 0, 0, 1.048, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '1.023, 0, 0, 0, 0, 1.023, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '0.996, 0, 0, 0, 0, 0.996, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '0.985, 0, 0, 0, 0, 0.985, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '0.965, 0, 0, 0, 0, 0.965, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '0.967, 0, 0, 0, 0, 0.967, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '0.979, 0, 0, 0, 0, 0.979, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
          '0.988, 0, 0, 0, 0, 0.988, 0, 0, 0, 0, 1, 0, 0, 0, 0, 1',
        ],
      });
    }
  },
};
</script>
